/**
 * Mapowanie statusu SENT (z urzad-api) na label PL, kolor Tailwind i sortKey.
 *
 * Status pochodzi z payload.status zwracanego przez `GET /sent/getSentStatus/:documentId`.
 * Jeśli zamówienie nie ma `invoice.sent.documentId` — zwracamy "Nie wysłano".
 *
 * Kolory Tailwind są spójne z resztą projektu (sprawdzić jakie classes używa Invoices.vue
 * dla badgy KSeF — sugerowana paleta: bg-{color}-100 text-{color}-800).
 */

const LABELS = {
  PENDING: 'Wysyłka w trakcie weryfikacji',
  CONFIRMED: 'Zatwierdzone, czeka na rozładunek',
  DELIVERED_AWAITING_RECIPIENT: 'Oczekuje na potwierdzenie odbiorcy',
  RECIPIENT_CONFIRMED: 'Zakończone',
  OVERDUE: 'Spóźnione',
  BUSINESS_ERROR: 'Wysyłka SENT nieudana',
  UNKNOWN: 'Status nieznany',
}

// Kolory dopasowane do nowego standardu wizualnego (ciemny slate, opacity layer).
const COLORS = {
  not_sent: 'bg-slate-700 bg-opacity-70 text-slate-300 border border-slate-600',
  PENDING: 'bg-blue-500 bg-opacity-20 text-blue-300 border border-blue-500 border-opacity-40',
  // Tailwind 2 config mapuje: yellow→amber palette, green→emerald palette. Brak cyan/amber/emerald pod własnymi nazwami.
  CONFIRMED: 'bg-blue-500 bg-opacity-20 text-blue-200 border border-blue-500 border-opacity-40',
  DELIVERED_AWAITING_RECIPIENT: 'bg-yellow-500 bg-opacity-20 text-yellow-300 border border-yellow-500 border-opacity-40',
  RECIPIENT_CONFIRMED: 'bg-green-500 bg-opacity-20 text-green-300 border border-green-500 border-opacity-40',
  OVERDUE: 'bg-red-500 bg-opacity-25 text-red-300 border border-red-500 border-opacity-50',
  BUSINESS_ERROR: 'bg-red-500 bg-opacity-25 text-red-300 border border-red-500 border-opacity-50',
  UNKNOWN: 'bg-slate-700 bg-opacity-70 text-slate-300 border border-slate-600',
}

// Sortowanie asc → najgorsze (BUSINESS_ERROR) na górze, "Zakończone" na końcu, brak na samym końcu.
const SORT_KEYS = {
  BUSINESS_ERROR: 0,
  OVERDUE: 1,
  DELIVERED_AWAITING_RECIPIENT: 2,
  PENDING: 3,
  CONFIRMED: 4,
  RECIPIENT_CONFIRMED: 5,
  UNKNOWN: 6,
  not_sent: 7,
}

/**
 * @param {string|null|undefined} status - payload.status z getSentStatus, lub null/undefined gdy brak invoice.sent
 * @returns {string} label PL
 */
export function getSentLabel(status) {
  if (!status) return 'Nie wysłano'
  return LABELS[status] || LABELS.UNKNOWN
}

/**
 * @param {string|null|undefined} status
 * @returns {string} Tailwind classes dla badge'a
 */
export function getSentColor(status) {
  if (!status) return COLORS.not_sent
  return COLORS[status] || COLORS.UNKNOWN
}

/**
 * @param {string|null|undefined} status
 * @returns {number} dla `sortedOrders` — niższe wartości na górze (asc)
 */
export function getSentSortKey(status) {
  if (!status) return SORT_KEYS.not_sent
  return SORT_KEYS[status] !== undefined ? SORT_KEYS[status] : SORT_KEYS.UNKNOWN
}

/**
 * Tooltip dla badge'a SENT — dla BUSINESS_ERROR doklejamy ostatni error.description
 * (z NPP / SENT_500), żeby admin nie musiał otwierać historii SENT żeby zobaczyć powód.
 *
 * @param {string|null|undefined} status
 * @param {Array<{code, description}>|null|undefined} errors - errors[] z payload.errors
 * @returns {string}
 */
export function getSentTooltip(status, errors) {
  const label = getSentLabel(status)
  if (status === 'BUSINESS_ERROR') {
    const reason = errors?.[0]?.description || errors?.[0]?.message
    if (reason) return `${label} — ${reason}`
    return `${label} — sprawdź historię SENT po szczegóły.`
  }
  return label
}

/**
 * Helper: ile godzin do deadline'u. Backend zwraca callback.deadline.unix w odpowiedzi
 * getSentStatus (jeśli markDelivered było). Frontend może użyć do "Zbliża się koniec czasu"
 * (≤24h do deadline).
 */
export function hoursUntilDeadline(sentStatusPayload) {
  const deadlineUnix = sentStatusPayload?.callback?.deadline?.unix ||
                       sentStatusPayload?.deadline?.unix
  if (!deadlineUnix) return null
  const nowUnix = Math.floor(Date.now() / 1000)
  return (deadlineUnix - nowUnix) / 3600
}
